import { useEffect, useId, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useDataApiClient } from 'api/data/client-dataapi';
import { ENV_VARS } from 'app/config/environment-vars';
import { useUserEmail } from 'state/appState/appStateHooks';
import { useGridActions } from 'state/grids/gridState';
import { useReleaseTemplateWizardActions } from 'state/releaseTemplateWizard/releaseTemplateWizardActions';
import { NotificationApiActions } from 'state/signalr/NotificationApiActions';
import type { SignalRActions } from 'state/signalr/SignalRActions';

/**
 * @param signalRActions the DataHub connection - some NotificationApi-pushed events (e.g.
 * DataNodeDeleted, RelationshipsChanged, RootRowAdded) still need to call back into it (e.g.
 * RequestRootRows) to fetch the data the completion event doesn't carry itself.
 */
export const useNotificationApiActions = (signalRActions: SignalRActions) => {
  const userEmail = useUserEmail();
  const gridActions = useGridActions();
  const dataApiClient = useDataApiClient();
  const history = useHistory();
  const releaseTemplateWizardActions = useReleaseTemplateWizardActions();
  const queryClient = useQueryClient();
  const id = useId();

  const notificationSignalrUrl = ENV_VARS.NOTIFICATION_SIGNALR_URL;

  const notificationApiActions = useMemo(() => {
    return new NotificationApiActions(
      id,
      userEmail,
      notificationSignalrUrl,
      gridActions,
      dataApiClient,
      history,
      releaseTemplateWizardActions,
      queryClient,
      signalRActions
    );
  }, [
    id,
    userEmail,
    notificationSignalrUrl,
    gridActions,
    dataApiClient,
    history,
    releaseTemplateWizardActions,
    queryClient,
    signalRActions
  ]);

  useEffect(() => {
    notificationApiActions?.Connect();
    // Mirrors useSignalRActions: cleanup intentionally omitted, see that hook's comment.
  }, [notificationApiActions]);

  return notificationApiActions;
};
